// Deterministic replays for Lesson 4.14 ("One Assistant or Several"). The venue question, its
// two answers and the cached-token field below were recorded on 9 October 2026 with zai.glm-5
// through the gateway: the generalist with thirteen tools against the notes specialist with one.
// The cost and cache graphics compute from those recorded totals. Nothing here calls a model.

export type Tone = 'neutral' | 'success' | 'warning';
export interface Summary {
  message: string;
  tone: Tone;
}
const tokens = (value: number) => value.toLocaleString('en-US');

// --- multi-handoff: the specialist against the generalist --------------------------------

export const ANSWERERS = ['generalist', 'specialist'] as const;
export type Answerer = (typeof ANSWERERS)[number];
export const answererLabels: Record<Answerer, string> = {
  generalist: 'the generalist with thirteen tools',
  specialist: 'the notes specialist with one',
};
export const toAnswerer = (value: string): Answerer =>
  ANSWERERS.find((id) => id === value) ?? 'generalist';
export const VENUE_QUESTION = 'What did we decide about the venue, and when do the badges arrive?';
export const VENUE_ANSWER =
  'The demo will be in the main hall [1.2][4.1], and the badges ship and arrive on the 9th [12.1].';
export interface HandoffCall {
  what: string;
}
export interface HandoffRun {
  tools: number;
  systemMessage: string;
  calls: readonly HandoffCall[];
  promptTokens: number;
  answer: string;
}
const VENUE_CALLS: readonly HandoffCall[] = [
  { what: 'search_notes for the venue decision and the badges' },
  { what: 'the answer with three citations' },
];
export const handoffRuns: Record<Answerer, HandoffRun> = {
  generalist: {
    tools: 13,
    systemMessage:
      'the full rule book: tasks against notes, approvals, citations, abstention, memory',
    calls: VENUE_CALLS,
    promptTokens: 4_490,
    answer: VENUE_ANSWER,
  },
  specialist: {
    tools: 1,
    systemMessage: 'four sentences about the notes, citations and abstention',
    calls: VENUE_CALLS,
    promptTokens: 1_090,
    answer: VENUE_ANSWER,
  },
};
/** The recorded total split evenly, since only the total was recorded. */
export const tokensPerCall = (run: HandoffRun) => Math.round(run.promptTokens / run.calls.length);
export const promptText = (run: HandoffRun) =>
  `${run.tools === 1 ? 'one tool' : `${run.tools} tools`}, ${run.systemMessage}`;
export interface HandoffState {
  answerer: Answerer;
  asked: boolean;
}
export const initialHandoff = (): HandoffState => ({ answerer: 'generalist', asked: false });
export type HandoffAction =
  { type: 'answerer'; value: Answerer } | { type: 'ask' } | { type: 'reset' };
export function handoffTransition(state: HandoffState, action: HandoffAction): HandoffState {
  switch (action.type) {
    case 'reset':
      return initialHandoff();
    case 'answerer':
      return { answerer: action.value, asked: false };
    case 'ask':
      return { ...state, asked: true };
  }
}
export const HANDOFF_START_MESSAGE = 'Choose who answers, then press Ask the venue question.';
export const GENERALIST_MESSAGE =
  'Two calls and 4,490 prompt tokens, with thirteen tools read on each.';
export const SPECIALIST_MESSAGE = 'The same answer for 1,090 prompt tokens, about a quarter.';
export const HANDOFF_REFINEMENT =
  "Refinement: the saving is the specialist's tool list, a quarter of the generalist's.";
export function handoffSummary(state: HandoffState): Summary {
  if (!state.asked) return { tone: 'neutral', message: HANDOFF_START_MESSAGE };
  return state.answerer === 'generalist'
    ? { tone: 'warning', message: GENERALIST_MESSAGE }
    : { tone: 'success', message: SPECIALIST_MESSAGE };
}

// --- multi-cost: when a handoff pays ----------------------------------------------------------

export const COST_QUESTIONS = ['notes', 'tasks', 'both'] as const;
export type CostQuestion = (typeof COST_QUESTIONS)[number];
export const costQuestionLabels: Record<CostQuestion, string> = {
  notes: 'notes only',
  tasks: 'tasks only',
  both: 'both',
};
export const toCostQuestion = (value: string): CostQuestion =>
  COST_QUESTIONS.find((id) => id === value) ?? 'notes';
export const LAYOUTS = ['one', 'split'] as const;
export type Layout = (typeof LAYOUTS)[number];
export const layoutLabels: Record<Layout, string> = {
  one: 'one assistant',
  split: 'orchestrator and specialist',
};
export const toLayout = (value: string): Layout => LAYOUTS.find((id) => id === value) ?? 'one';
/** The recorded 4,490 prompt tokens over the generalist's two calls. */
export const ORCHESTRATOR_STEP = 2_245;
/** The recorded 1,090 prompt tokens over the specialist's two calls. */
export const SPECIALIST_STEP = 545;
export type Loop = 'assistant' | 'orchestrator' | 'specialist';
export interface CostCall {
  loop: Loop;
  what: string;
  tokens: number;
}
const big = (loop: 'assistant' | 'orchestrator', what: string): CostCall => ({
  loop,
  what,
  tokens: ORCHESTRATOR_STEP,
});
const small = (what: string): CostCall => ({ loop: 'specialist', what, tokens: SPECIALIST_STEP });
const HANDOFF: readonly CostCall[] = [
  big('orchestrator', 'ask_notes: hand the question to the specialist'),
  small('search_notes'),
  small('the answer with citations, returned'),
];
export function costCalls(question: CostQuestion, layout: Layout): readonly CostCall[] {
  if (layout === 'one') {
    switch (question) {
      case 'notes':
        return [big('assistant', 'search_notes'), big('assistant', 'the answer')];
      case 'tasks':
        return [big('assistant', 'list_tasks'), big('assistant', 'the answer')];
      case 'both':
        return [
          big('assistant', 'search_notes'),
          big('assistant', 'list_tasks'),
          big('assistant', 'the answer'),
        ];
    }
  }
  switch (question) {
    case 'notes':
      return [...HANDOFF, big('orchestrator', 'relay the answer')];
    case 'tasks':
      return [big('orchestrator', 'list_tasks'), big('orchestrator', 'the answer')];
    case 'both':
      return [...HANDOFF, big('orchestrator', 'list_tasks'), big('orchestrator', 'the answer')];
  }
}
export const costTotal = (calls: readonly CostCall[]) =>
  calls.reduce((sum, call) => sum + call.tokens, 0);
export const handedOff = (calls: readonly CostCall[]) =>
  calls.some((call) => call.loop === 'specialist');
export const WRONG_STEP_TOOL = 'the tool choice at step 1, inside the one loop';
export const WRONG_STEP_SPECIALIST = 'the choice of specialist at step 1, one level up';
export const wrongStepLands = (calls: readonly CostCall[]) =>
  handedOff(calls) ? WRONG_STEP_SPECIALIST : WRONG_STEP_TOOL;
/** Only the notes question with one assistant is a recorded total, the others are about. */
export const costTokensText = (question: CostQuestion, layout: Layout) => {
  const total = tokens(costTotal(costCalls(question, layout)));
  return question === 'notes' && layout === 'one' ? total : `about ${total}`;
};
export interface CostState {
  question: CostQuestion;
  layout: Layout;
  computed: boolean;
}
export const initialCost = (): CostState => ({ question: 'notes', layout: 'one', computed: false });
export type CostAction =
  | { type: 'question'; value: CostQuestion }
  | { type: 'layout'; value: Layout }
  | { type: 'compute' }
  | { type: 'reset' };
export function costTransition(state: CostState, action: CostAction): CostState {
  switch (action.type) {
    case 'reset':
      return initialCost();
    case 'question':
      return { ...state, question: action.value, computed: false };
    case 'layout':
      return { ...state, layout: action.value, computed: false };
    case 'compute':
      return { ...state, computed: true };
  }
}
export const COST_START_MESSAGE = 'Choose a question and a layout, then press Compute.';
export const ONE_NOTES_MESSAGE =
  'Two calls and 4,490 prompt tokens, each step reading thirteen tools.';
export const ONE_TASKS_MESSAGE =
  'Two calls and about 4,490 prompt tokens, the same shape as the notes question.';
export const ONE_BOTH_MESSAGE =
  'Three calls and about 6,735 prompt tokens, one loop reading both sources.';
export const SPLIT_NOTES_MESSAGE =
  'The orchestrator kept its thirteen tools, so the handoff added 1,090 tokens.';
export const SPLIT_TASKS_MESSAGE = "No handoff ran, so the cost is the one assistant's.";
export const SPLIT_BOTH_MESSAGE =
  'Both loops ran, so the question paid five calls and about 7,825 tokens.';
export const COST_REFINEMENT =
  'Refinement: split by source only when most questions stay inside one specialist.';
export function costSummary(state: CostState): Summary {
  if (!state.computed) return { tone: 'neutral', message: COST_START_MESSAGE };
  if (state.layout === 'one')
    switch (state.question) {
      case 'notes':
        return { tone: 'neutral', message: ONE_NOTES_MESSAGE };
      case 'tasks':
        return { tone: 'neutral', message: ONE_TASKS_MESSAGE };
      case 'both':
        return { tone: 'neutral', message: ONE_BOTH_MESSAGE };
    }
  switch (state.question) {
    case 'notes':
      return { tone: 'warning', message: SPLIT_NOTES_MESSAGE };
    case 'tasks':
      return { tone: 'neutral', message: SPLIT_TASKS_MESSAGE };
    case 'both':
      return { tone: 'warning', message: SPLIT_BOTH_MESSAGE };
  }
}

// --- multi-guardrail: what leaves the assistant ---------------------------------------------

export const REPLIES = ['plain', 'link', 'file'] as const;
export type Reply = (typeof REPLIES)[number];
export const replyLabels: Record<Reply, string> = {
  plain: 'a plain answer',
  link: 'an answer with a link carrying data',
  file: 'a tool that writes a file',
};
export const toReply = (value: string): Reply => REPLIES.find((id) => id === value) ?? 'plain';
export interface ReplyItem {
  what: string;
  /** What the guardrail looks at. */
  check: string;
  /** Why the guardrail stops it, or null when it passes the check. */
  blocked: string | null;
  /** What happens with the guardrail off. */
  unchecked: string;
}
const item = (what: string, check: string, unchecked: string, blocked: string | null = null) => ({
  what,
  check,
  blocked,
  unchecked,
});
export const SANDBOX = "a sandbox with the task's files, no network, a 30-second timeout";
export const replyItems: Record<Reply, readonly ReplyItem[]> = {
  plain: [
    item(
      'the answer: "The demo will be in the main hall [1.2][4.1]."',
      'no outside link, no address built from data',
      'leaves',
    ),
    item('the citations [1.2][4.1]', 'pieces Maya may read', 'leaves'),
  ],
  link: [
    item('the answer text', 'no outside link, no address built from data', 'leaves'),
    item(
      "a link to an outside site with Maya's memory text in its address",
      "links only to the application's own pages",
      'leaves, and no tool ran for approval to see',
      'the address was built from data',
    ),
  ],
  file: [
    item(
      'write_file("export/notes.txt") from the shell tool',
      'runs in ' + SANDBOX,
      "runs with the account's full rights and no time limit",
    ),
  ],
};
export type Outcome = 'passes' | 'blocked' | 'unchecked';
export const itemOutcome = (entry: ReplyItem, guardrail: boolean): Outcome =>
  !guardrail ? 'unchecked' : entry.blocked === null ? 'passes' : 'blocked';
export const outcomeText = (entry: ReplyItem, guardrail: boolean) => {
  switch (itemOutcome(entry, guardrail)) {
    case 'unchecked':
      return entry.unchecked;
    case 'passes':
      return `passes: ${entry.check}`;
    case 'blocked':
      return `blocked: ${entry.blocked ?? ''}`;
  }
};
export const passedCount = (reply: Reply, guardrail: boolean) =>
  replyItems[reply].filter((entry) => itemOutcome(entry, guardrail) !== 'blocked').length;
export const blockedCount = (reply: Reply, guardrail: boolean) =>
  replyItems[reply].filter((entry) => itemOutcome(entry, guardrail) === 'blocked').length;
export const whyText = (reply: Reply, guardrail: boolean) => {
  if (!guardrail) return 'no check ran';
  const stopped = replyItems[reply].find((entry) => entry.blocked !== null);
  return stopped?.blocked ?? 'every check passed';
};
export interface GuardrailState {
  reply: Reply;
  guardrail: boolean;
  sent: boolean;
}
export const initialGuardrail = (): GuardrailState => ({
  reply: 'plain',
  guardrail: false,
  sent: false,
});
export type GuardrailAction =
  | { type: 'reply'; value: Reply }
  | { type: 'toggle-guardrail' }
  | { type: 'send' }
  | { type: 'reset' };
export function guardrailTransition(
  state: GuardrailState,
  action: GuardrailAction,
): GuardrailState {
  switch (action.type) {
    case 'reset':
      return initialGuardrail();
    case 'reply':
      return { ...state, reply: action.value, sent: false };
    case 'toggle-guardrail':
      return { ...state, guardrail: !state.guardrail, sent: false };
    case 'send':
      return { ...state, sent: true };
  }
}
export const GUARDRAIL_START_MESSAGE = 'Choose a reply, then press Send.';
export const PLAIN_MESSAGE =
  'The plain answer leaves either way, because nothing in it carries data out.';
export const LINK_LEAKED_MESSAGE =
  "The link left with Maya's data in its address, and no tool ran.";
export const LINK_BLOCKED_MESSAGE =
  'The output check blocked the link whose address was built from data.';
export const FILE_UNBOUNDED_MESSAGE =
  "The write ran with the account's full rights and no time limit.";
export const FILE_SANDBOXED_MESSAGE =
  "The write ran in a sandbox with the task's files and a timeout.";
export const GUARDRAIL_REFINEMENT =
  'Refinement: an output check for replies, a sandbox for tools that touch the world.';
export function guardrailSummary(state: GuardrailState): Summary {
  if (!state.sent) return { tone: 'neutral', message: GUARDRAIL_START_MESSAGE };
  switch (state.reply) {
    case 'plain':
      return { tone: 'success', message: PLAIN_MESSAGE };
    case 'link':
      return state.guardrail
        ? { tone: 'success', message: LINK_BLOCKED_MESSAGE }
        : { tone: 'warning', message: LINK_LEAKED_MESSAGE };
    case 'file':
      return state.guardrail
        ? { tone: 'success', message: FILE_SANDBOXED_MESSAGE }
        : { tone: 'warning', message: FILE_UNBOUNDED_MESSAGE };
  }
}

// --- multi-cache: the same prefix on every step ----------------------------------------------

export const STEP_COUNTS = [2, 6] as const;
export type StepCount = (typeof STEP_COUNTS)[number];
export const stepCountLabels: Record<StepCount, string> = { 2: 'two', 6: 'six' };
export const toStepCount = (value: string): StepCount =>
  STEP_COUNTS.find((count) => String(count) === value) ?? 2;
export const CACHE_REPORTS = ['none', 'recorded'] as const;
export type CacheReport = (typeof CACHE_REPORTS)[number];
export const cacheReportLabels: Record<CacheReport, string> = {
  none: 'none',
  recorded: '1,920 as measured',
};
export const toCacheReport = (value: string): CacheReport =>
  CACHE_REPORTS.find((id) => id === value) ?? 'none';
/** The recorded 4,490 prompt tokens over two calls carrying the tool block. */
export const STEP_PROMPT_TOKENS = 2_245;
/** prompt_tokens_details.cached_tokens on two of four recorded calls. */
export const RECORDED_CACHED = 1_920;
export interface CacheStep {
  step: number;
  sent: number;
  cached: number;
}
/** The first step fills the cache, so each later step is the one that can report a read. */
export const cacheSteps = (steps: StepCount, report: CacheReport): readonly CacheStep[] =>
  Array.from({ length: steps }, (_, index) => ({
    step: index + 1,
    sent: STEP_PROMPT_TOKENS,
    cached: report === 'recorded' && index > 0 ? RECORDED_CACHED : 0,
  }));
export const sentTotal = (steps: readonly CacheStep[]) =>
  steps.reduce((sum, step) => sum + step.sent, 0);
export const cachedTotal = (steps: readonly CacheStep[]) =>
  steps.reduce((sum, step) => sum + step.cached, 0);
export const cachedShareText = (steps: readonly CacheStep[]) =>
  `${Math.round((cachedTotal(steps) / sentTotal(steps)) * 100)}%`;
export const stepsReporting = (steps: readonly CacheStep[]) =>
  steps.filter((step) => step.cached > 0).length;
export interface CacheState {
  steps: StepCount;
  report: CacheReport;
  computed: boolean;
}
export const initialCache = (): CacheState => ({ steps: 2, report: 'none', computed: false });
export type CacheAction =
  | { type: 'steps'; value: StepCount }
  | { type: 'report'; value: CacheReport }
  | { type: 'compute' }
  | { type: 'reset' };
export function cacheTransition(state: CacheState, action: CacheAction): CacheState {
  switch (action.type) {
    case 'reset':
      return initialCache();
    case 'steps':
      return { ...state, steps: action.value, computed: false };
    case 'report':
      return { ...state, report: action.value, computed: false };
    case 'compute':
      return { ...state, computed: true };
  }
}
export const CACHE_START_MESSAGE =
  'Choose the steps and the cached tokens reported, then press Compute.';
export const CACHE_NONE_MESSAGE =
  'The usage reported no cached tokens, so no saving can be counted.';
export const CACHE_TWO_MESSAGE = 'One step of two reported 1,920 cached tokens of 4,490 sent.';
export const CACHE_SIX_MESSAGE =
  'Five steps of six reported the tool block cached, 9,600 of 13,470 tokens.';
export const CACHE_REFINEMENT =
  'Refinement: stable prefix first, record the field, and count the saving from the bill.';
export function cacheSummary(state: CacheState): Summary {
  if (!state.computed) return { tone: 'neutral', message: CACHE_START_MESSAGE };
  if (state.report === 'none') return { tone: 'warning', message: CACHE_NONE_MESSAGE };
  return state.steps === 2
    ? { tone: 'neutral', message: CACHE_TWO_MESSAGE }
    : { tone: 'success', message: CACHE_SIX_MESSAGE };
}
